import React, { useState } from 'react';
import classNames from 'classnames';

import { createClickHandler } from '@whelk/utils/src/event';
import { useRenameHistory, useHistoryItem } from '@whelk/hooks/src/useHistoryList';
import usePageName from '@whelk/hooks/src/usePageName';

import { isMobileByUA } from '@whelk/utils/src/checkUA';
import { CHAT_TITLE_MAX_LENGTH, HISTORY_OPERATE_TYPE } from '@whelk/const/src/history';

import MenuDropdown from '@whelk/components/src/HistoryList/components/WebHistoryItem/MenuDropdown';
import { trackOpClick } from '@whelk/components/src/HistoryList/components/WebHistoryItem/track';

import './style.scss';
import { useSidebar } from '@whelk/components';

interface HistoryItemProps {
  chatId: string;
  isCurrentChat: boolean;
}

const HistoryItem: React.FC<HistoryItemProps> = ({ chatId, isCurrentChat }) => {
  const { getPageName } = usePageName();
  const { handleMobileClose } = useSidebar();
  const { historyItem: item, goChatHistory } = useHistoryItem(chatId);
  const { confirmRenameChat } = useRenameHistory();
  const chatTitle = (item?.chatTitle ?? '').slice(0, CHAT_TITLE_MAX_LENGTH);
  const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const trackParams = {
    page: getPageName(),
    chat_id: item.chatID,
  };

  const handleRename = (newTitle: string) => {
    setIsEditing(false);

    if (newTitle && newTitle !== chatTitle) {
      confirmRenameChat(item.chatID, newTitle, trackParams);
    }
  };

  const handleItemClick = () => {
    if (isEditing) {
      return;
    }
    handleMobileClose();
    goChatHistory(chatId);
  };

  const handleDoubleClick = () => {
    if (isMobileByUA) {
      return;
    }
    setEditValue(chatTitle);
    setIsEditing(true);
    trackOpClick(HISTORY_OPERATE_TYPE.DC_RENAME);
  };

  const handleClick = createClickHandler(handleItemClick, handleDoubleClick);

  const handleBlur = () => {
    handleRename(editValue);
  };

  const handleKeyDown = (e: any) => {
    if (isEditing && e.key === 'Enter') {
      handleRename(editValue);
    }
  };

  const onContextMenuClick = (e: React.MouseEvent) => {
    if (isMobileByUA) {
      return;
    }
    e.preventDefault();
    if (!contextMenuOpen) {
      setContextMenuOpen(true);
    }
  };

  return isEditing ? (
    <input
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className='history-title-input bg-col_bg13 border-col_brand02 inset-0 mb-[2px] h-9 w-full truncate rounded-lg border px-[9px] focus:outline-none'
      autoFocus
    />
  ) : (
    <>
      <MenuDropdown
        item={item}
        historyItemClassName={`md:pr-[12px] pl-[10px] mb-[2px]  ${
          isCurrentChat ? 'bg-col_bg13' : 'md:hover:bg-col_bg14 '
        } $`}
        className={classNames(
          'md:hover:text-col_text00 ml-3 md:hidden md:group-hover/history:block',
        )}
      >
        <div
          className={classNames(
            'history/hover:bg-col_bg02 flex h-full flex-1 items-center truncate',
            {
              'text-col_text00': isCurrentChat,
            },
          )}
          onClick={handleClick}
          onContextMenu={onContextMenuClick}
        >
          <span className='block w-full truncate' title={item.chatTitle}>
            {item.chatTitle}
          </span>
        </div>
      </MenuDropdown>
    </>
  );
};

export default HistoryItem;
